import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Building2,
  UserX,
  Clock,
  CalendarPlus,
  AlertCircle,
  Users,
} from "lucide-react";
import type { QuickFilterCounts } from "@/types/superAdmin";

interface QuickFiltersProps {
  counts: QuickFilterCounts;
  activeFilter: string | null;
  onFilterChange: (filter: string | null) => void;
  isLoading?: boolean;
}

const filterDefs = [
  { id: "multiGym", icon: Building2, color: "bg-status-info text-status-info-foreground hover:bg-status-info border-status-info-border" },
  { id: "noSubscription", icon: UserX, color: "bg-status-warning text-status-warning-foreground hover:bg-status-warning border-status-warning-border" },
  { id: "neverConnected", icon: Clock, color: "bg-status-danger text-status-danger-foreground hover:bg-status-danger border-status-danger-border" },
  { id: "inactive30d", icon: AlertCircle, color: "bg-status-warning text-status-warning-foreground hover:bg-status-warning border-status-warning-border" },
  { id: "newThisWeek", icon: CalendarPlus, color: "bg-status-success text-status-success-foreground hover:bg-status-success border-status-success-border" },
  { id: "expiredSubscription", icon: Users, color: "bg-status-info text-status-info-foreground hover:bg-status-info border-status-info-border" },
];

export function QuickFilters({
  counts,
  activeFilter,
  onFilterChange,
  isLoading,
}: QuickFiltersProps) {
  const { t } = useTranslation("adminui3");
  const isMobile = useIsMobile();

  const filters = filterDefs.map((f) => ({
    ...f,
    label: t(`superAdminQuickFilters.${f.id}.label`),
    shortLabel: t(`superAdminQuickFilters.${f.id}.shortLabel`),
  }));

  return (
    <div className={cn(
      "flex flex-wrap gap-2",
      isMobile && "gap-1"
    )}>
      {filters.map((filter) => {
        const Icon = filter.icon;
        const count = counts[filter.id as keyof QuickFilterCounts] || 0;
        const isActive = activeFilter === filter.id;

        return (
          <Button
            key={filter.id}
            variant="outline"
            size={isMobile ? "sm" : "default"}
            onClick={() => onFilterChange(isActive ? null : filter.id)}
            className={cn(
              "transition-all duration-200 border",
              filter.color,
              isActive && "ring-2 ring-ring ring-offset-2 ring-offset-background",
              isMobile && "text-xs px-2 h-8"
            )}
            disabled={isLoading}
          >
            <Icon className={cn("mr-1", isMobile ? "h-3 w-3" : "h-4 w-4")} />
            <span>{isMobile ? filter.shortLabel : filter.label}</span>
            <Badge
              variant="secondary"
              className={cn(
                "ml-1",
                isMobile ? "text-[10px] px-1 py-0 h-4" : "text-xs",
                 isActive && "bg-background text-foreground"
              )}
            >
              {count}
            </Badge>
          </Button>
        );
      })}

      {activeFilter && (
        <Button
          variant="ghost"
          size={isMobile ? "sm" : "default"}
          onClick={() => onFilterChange(null)}
          className={cn(
            "text-muted-foreground hover:text-foreground",
            isMobile && "text-xs px-2 h-8"
          )}
        >
          {t("superAdminQuickFilters.clear")}
        </Button>
      )}
    </div>
  );
}
